// Session-independent client error logger.
//
// Why this exists: the only thing that logged errors before was the React
// ErrorBoundary (render crashes only), and it wrote via the user's session — so an
// auth/session collapse (the exact thing worth logging) produced ZERO entries: the
// failure died inside supabase-js with no listener, and any log attempt rode the
// same dead token.
//
// Delivery is a fallback chain:
//   1. authenticated RPC (`log_error`)        — normal, healthy session
//   2. session-FREE edge function             — anon key only, never the user's
//      (`log-client-error`)                      possibly-dead Authorization header
//   3. in-memory queue                        — flushed on session recovery
// Logging never throws and never affects the user-facing flow.
//
// Self-contained (own clock-skew estimate, own auth listener) for portability to
// bones-repo and to avoid coupling to other auth code.

import { redactValue, sanitizeClientErrorReport, type ClientErrorReport, type ErrorType } from './errorRedaction';

type RpcResult = { data: unknown; error: unknown };
interface SessionLike { access_token?: string; expires_at?: number }
interface SupabaseLike {
  rpc: (fn: string, args: Record<string, unknown>) => Promise<RpcResult> | { then: (...a: unknown[]) => unknown };
  auth: {
    getSession: () => Promise<{ data: { session: SessionLike | null } }>;
    onAuthStateChange: (
      cb: (event: string, session: SessionLike | null) => void,
    ) => { data: { subscription: { unsubscribe: () => void } } };
  };
}

export interface ErrorLoggerConfig {
  supabase: SupabaseLike;
  supabaseUrl: string;
  anonKey: string;
  /** Injectable for tests; defaults to global fetch. */
  fetchImpl?: typeof fetch;
  /** The app's one server-clock estimate (authResilience's getClockSkewMs). The
   *  logger used to keep its own, taken from every SIGNED_IN token, and a tab
   *  refocus carries the token it already had, so a 34-minute-old token read as a
   *  34-minute clock error on every card (Drew, 2026-11-04). Omitted = 0. */
  clockSkewMs?: () => number;
}

const MAX_QUEUE = 50;
const INTENTIONAL_SIGNOUT_WINDOW_MS = 5_000;

// Cross-tab auth-lock steal handling — "Lock broken by another request with the
// 'steal' option" is the Web Locks AbortError in the tab that LOSES the refresh
// lock (client.ts) when auth-js force-recovers an orphaned holder after 5s.
//
// A SINGLE steal is the designed recovery path: every query waiting in the
// losing tab rejects at the same instant (one event fans out into several
// messages), React Query retries against the healthy session, and nothing is
// lost. That logs NOTHING — rejections within STEAL_COALESCE_MS collapse into
// one incident, and isolated incidents are dropped.
//
// REPEATED steals mean the recovery is spinning — something keeps grabbing the
// lock and hanging (stuck tab, wedged service worker). That is a real
// malfunction: STEAL_STORM_THRESHOLD distinct incidents inside
// STEAL_STORM_WINDOW_MS escalate into ONE logged auth error (diagnosed as
// "Auth Lock Contention Storm" in errorDiagnosis.ts).
//
// Firefox words the same AbortError "The operation was aborted." (Andrew,
// 2026-11-03: one such row from the All Clients list, logged because only the
// Chrome wording was known). Nothing else in the app rejects that way: no query
// passes an abort signal, and React Query's own cancellations never reach the
// global catch. Drew: "I only want things that will break the app."
const LOCK_STEAL_PATTERN = /lock broken by another request|the operation was aborted/i;
const STEAL_COALESCE_MS = 10_000; // rejections this close together = one steal
const STEAL_STORM_WINDOW_MS = 10 * 60_000;
const STEAL_STORM_THRESHOLD = 3; // distinct steals in the window = malfunction
let lastStealIncidentAt = 0;
let stealIncidents: number[] = [];

function trackLockSteal(): { escalate: boolean; count: number } {
  const now = Date.now();
  if (now - lastStealIncidentAt < STEAL_COALESCE_MS) {
    return { escalate: false, count: stealIncidents.length }; // same burst — same steal
  }
  lastStealIncidentAt = now;
  stealIncidents = stealIncidents.filter((t) => now - t < STEAL_STORM_WINDOW_MS);
  stealIncidents.push(now);
  if (stealIncidents.length >= STEAL_STORM_THRESHOLD) {
    const count = stealIncidents.length;
    stealIncidents = []; // one alarm per storm, not one per steal
    return { escalate: true, count };
  }
  return { escalate: false, count: stealIncidents.length };
}

// A script that is not ours — browser automation (Puppeteer/Playwright's
// wait-for-condition poller), an extension, a scanner — asking Chrome to run
// code from a string, which `script-src 'self'` refuses. Our bundle never evals,
// so that refusal is only ever the policy doing its job on foreign code, and it
// landed in the Errors panel as a "React crash" for 5 signed-out browsers on
// /auth (2026-10-29). Dropped only when NO frame of the stack comes from our
// own bundle: a dependency that evals from inside /assets/ is a real bug and
// still logs.
const CSP_EVAL_REFUSAL = /refused to evaluate a string as javascript/i;
const OWN_BUNDLE_FRAME = /\/assets\//;
export function isForeignCspEvalRefusal(message: string, stack: string | null | undefined): boolean {
  return CSP_EVAL_REFUSAL.test(message) && !OWN_BUNDLE_FRAME.test(stack ?? '');
}

const USER_AGENT_MAX = 512;

// "ResizeObserver loop completed with undelivered notifications." (Chrome;
// Firefox's older wording ends "loop limit exceeded") is the browser saying a
// chart or popover changed size inside the same frame its size was observed in,
// so it delivered the rest next frame. Nothing failed and nothing was lost; the
// browser fires it on the window as an error anyway. Drew, 2026-11-04, three
// of them from his own dashboard: "I only want things that will break the app."
const RESIZE_OBSERVER_LOOP = /^ResizeObserver loop/i;
export function isResizeObserverLoop(message: string | null | undefined): boolean {
  return RESIZE_OBSERVER_LOOP.test((message ?? '').trim());
}

let cfg: ErrorLoggerConfig | null = null;
let clientId = 'unknown';
let intentionalSignOutUntil = 0;
const queue: ClientErrorReport[] = [];

/** The app's Sign Out handler calls this right before signing out, so the
 *  SIGNED_OUT that follows is treated as a normal user action and NOT logged. */
export function markIntentionalSignOut(): void {
  intentionalSignOutUntil = Date.now() + INTENTIONAL_SIGNOUT_WINDOW_MS;
}

/** Wire the logger and start capturing auth-layer failures. Returns a teardown fn. */
export function initErrorLogging(config: ErrorLoggerConfig): () => void {
  cfg = config;
  clientId = getOrCreateClientId();
  const { data } = config.supabase.auth.onAuthStateChange((event, session) => {
    if ((event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') && session?.access_token) {
      flushQueue();
    }
    if (event === 'SIGNED_OUT') {
      // A user-clicked Sign Out is normal — never log it. Only an INVOLUNTARY
      // sign-out (token refresh failure / revoked session) is worth capturing; it
      // otherwise dies silently inside supabase-js.
      if (Date.now() < intentionalSignOutUntil) { intentionalSignOutUntil = 0; return; }
      void logClientError({ error_message: 'auth: involuntary sign-out (token refresh failed or session revoked)', error_type: 'auth' });
    }
  });

  // Button/handler/async crashes that React's ErrorBoundary can't see (it only
  // catches render-phase errors).
  const onWindowError = (e: ErrorEvent) => {
    if (isResizeObserverLoop(e.message)) return;
    void logClientError({ error_message: `uncaught: ${e.message}`, stack_trace: e.error?.stack ?? null, error_type: 'crash' });
  };
  const onRejection = (e: PromiseRejectionEvent) => {
    const r = e.reason as { message?: string; stack?: string } | undefined;
    void logClientError({ error_message: `unhandled rejection: ${r?.message ?? String(e.reason)}`, stack_trace: r?.stack ?? null, error_type: 'crash' });
  };
  if (typeof window !== 'undefined') {
    window.addEventListener('error', onWindowError);
    window.addEventListener('unhandledrejection', onRejection);
  }

  return () => {
    try { data.subscription.unsubscribe(); } catch { /* noop */ }
    if (typeof window !== 'undefined') {
      window.removeEventListener('error', onWindowError);
      window.removeEventListener('unhandledrejection', onRejection);
    }
  };
}

function getOrCreateClientId(): string {
  try {
    const KEY = 'ttg-client-id';
    let id = localStorage.getItem(KEY);
    if (!id) {
      id = (globalThis.crypto?.randomUUID?.() ?? `c_${Date.now()}_${Math.random().toString(36).slice(2)}`);
      localStorage.setItem(KEY, id);
    }
    return id;
  } catch {
    return 'no-storage';
  }
}

/** The browser (or bot) that sent the report. Rides inside auth_diagnostics so
 *  no column, RPC signature or edge function has to change. */
function userAgent(): string | null {
  try {
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : null;
    return ua ? ua.slice(0, USER_AGENT_MAX) : null;
  } catch {
    return null;
  }
}

function skewMs(): number {
  try { return cfg?.clockSkewMs?.() ?? 0; } catch { return 0; }
}

/** Whether a session exists, token expiry, time-to-expiry by the client clock, skew, and the user agent. */
async function collectAuthDiagnostics(): Promise<Record<string, unknown>> {
  try {
    const { data } = await cfg!.supabase.auth.getSession();
    const session = data.session;
    const expSec = session?.expires_at ?? null;
    const nowSec = Math.round(Date.now() / 1000);
    const clockSkewMs = skewMs();
    return {
      sessionExists: !!session,
      tokenExpiresAt: expSec ? new Date(expSec * 1000).toISOString() : null,
      secondsToExpiry: expSec != null ? expSec - nowSec : null, // client clock
      clockSkewMs,
      serverNowEstimate: new Date(Date.now() + clockSkewMs).toISOString(),
      userAgent: userAgent(),
    };
  } catch {
    return { sessionExists: null, tokenExpiresAt: null, secondsToExpiry: null, clockSkewMs: skewMs(), userAgent: userAgent() };
  }
}

/** Log a client error. Never throws. */
export async function logClientError(
  partial: Partial<ClientErrorReport> & { error_message: string },
): Promise<void> {
  try {
    if (!cfg) return;
    if (isForeignCspEvalRefusal(partial.error_message, partial.stack_trace)) return;
    if (LOCK_STEAL_PATTERN.test(partial.error_message)) {
      const { escalate, count } = trackLockSteal();
      if (!escalate) return; // single/rare steal — designed recovery, not an error
      partial = {
        error_message:
          `auth: lock-steal storm — ${count} cross-tab auth-lock recoveries within 10 minutes. ` +
          'Something is repeatedly holding the refresh lock and hanging (stuck tab or wedged service worker). ' +
          'Isolated recoveries are normal and not logged; this repetition is a malfunction.',
        error_type: 'auth',
      };
    }
    const report = sanitizeClientErrorReport({
      error_message: partial.error_message,
      stack_trace: partial.stack_trace ?? null,
      page: partial.page ?? (typeof window !== 'undefined' ? window.location?.pathname ?? null : null),
      query_name: partial.query_name ?? null,
      http_status: partial.http_status ?? null,
      input_data: partial.input_data ?? null,
      error_type: (partial.error_type ?? 'crash') as ErrorType,
      auth_diagnostics: await collectAuthDiagnostics(),
      client_id: clientId,
    });
    await deliver(report);
  } catch {
    // A logging failure must never surface to the user.
  }
}

async function deliver(report: ClientErrorReport): Promise<void> {
  // 1. Authenticated path — healthy session.
  try {
    const res = (await cfg!.supabase.rpc('log_error', toRpcArgs(report))) as RpcResult;
    if (!res?.error) return;
    if (!isSessionProblem(res.error)) return; // a real validation/server error — don't spam the fallback
  } catch (e) {
    if (!isSessionProblem(e)) return;
  }
  // 2. Session-free edge path.
  if (await deliverViaEdge(report)) return;
  // 3. Queue for flush on recovery.
  enqueue(report);
}

/** 401/403/network/JWT means the session is the problem → worth the session-free fallback. */
function isSessionProblem(error: unknown): boolean {
  const e = error as { status?: number; message?: string };
  if (e?.status === 401 || e?.status === 403) return true;
  const m = (e?.message ?? String(error ?? '')).toLowerCase();
  return /jwt|unauthorized|forbidden|permission denied|row-level security|42501|network|fetch failed|refresh_token|failed to fetch/.test(m);
}

async function deliverViaEdge(report: ClientErrorReport): Promise<boolean> {
  try {
    const doFetch = cfg!.fetchImpl ?? fetch;
    const res = await doFetch(`${cfg!.supabaseUrl}/functions/v1/log-client-error`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // Anon key ONLY — deliberately not the user's (possibly dead) session token.
        apikey: cfg!.anonKey,
        Authorization: `Bearer ${cfg!.anonKey}`,
      },
      body: JSON.stringify(report),
    });
    return !!res?.ok;
  } catch {
    return false;
  }
}

function enqueue(report: ClientErrorReport): void {
  if (queue.length >= MAX_QUEUE) queue.shift(); // drop oldest
  queue.push(report);
}

function flushQueue(): void {
  if (!cfg || queue.length === 0) return;
  const pending = queue.splice(0, queue.length);
  for (const report of pending) {
    Promise.resolve(cfg.supabase.rpc('log_error', toRpcArgs(report)) as Promise<RpcResult>)
      .then((res) => { if (res?.error) enqueue(report); })
      .catch(() => enqueue(report));
  }
}

function toRpcArgs(r: ClientErrorReport): Record<string, unknown> {
  return {
    p_error_message: r.error_message,
    p_stack_trace: r.stack_trace ?? null,
    p_page: r.page ?? null,
    p_query_name: r.query_name ?? null,
    p_http_status: r.http_status ?? null,
    p_input_data: redactValue(r.input_data ?? null),
    p_error_type: r.error_type ?? 'crash',
    p_auth_diagnostics: r.auth_diagnostics ?? null,
    p_client_id: r.client_id ?? null,
  };
}

// ---- Test-only helpers ----
export function __resetErrorLogger(): void {
  cfg = null;
  clientId = 'unknown';
  intentionalSignOutUntil = 0;
  queue.length = 0;
  lastStealIncidentAt = 0;
  stealIncidents = [];
}
export function __queueLength(): number {
  return queue.length;
}
